import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserRole, Company } from '../types';
import { supabase } from '../lib/supabaseClient';
import { User, Session } from '@supabase/supabase-js';
import { getQueryClient } from '../providers/QueryProvider';
import { telemetry } from '../utils/telemetry';
import { isNetworkError } from '../utils/network';

interface AuthContextType {
    isAuthenticated: boolean;
    userRole: UserRole | null;
    currentTechnicianId: string | null;
    userEmail: string | null;
    login: (role: UserRole) => void;
    logout: () => void;
    isLoading: boolean;
    user: User | null;
    setUserRole: (role: UserRole) => void;
    mfaFactors: any[];
    mfaRequired: boolean;
    refreshMfaFactors: () => Promise<void>;
    enrollTOTP: () => Promise<{ id: string; qrCodeUrl: string; uri: string }>;
    verifyTOTP: (factorId: string, code: string) => Promise<void>;
    unenrollFactor: (factorId: string) => Promise<void>;
    company: Company | null;
    refreshCompany: () => Promise<void>;
    viewAsRole: UserRole | null;
    effectiveRole: UserRole | null;
    toggleViewRole: () => void;
}

import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

export const VIEW_AS_ROLE_KEY = 'pd_view_as_role';

const CACHE_KEY = 'pd_auth_session_v1';

export async function setOfflineCache(data: any) {
  try {
    const val = JSON.stringify(data);
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key: CACHE_KEY, value: val });
    } else {
      localStorage.setItem(CACHE_KEY, val);
    }
  } catch (err) {
    console.warn("setOfflineCache failed:", err);
  }
}

export async function getOfflineCache(): Promise<any | null> {
  try {
    let val: string | null = null;
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key: CACHE_KEY });
      val = value;
    } else {
      val = localStorage.getItem(CACHE_KEY);
    }
    return val ? JSON.parse(val) : null;
  } catch (err) {
    console.warn("getOfflineCache failed:", err);
    return null;
  }
}

export async function removeOfflineCache() {
  try {
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key: CACHE_KEY });
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  } catch (err) {
    console.warn("removeOfflineCache failed:", err);
  }
}

/**
 * Purga quirúrgica de sesión: elimina ÚNICAMENTE tokens de autenticación de Supabase
 * y caché de sesión local, preservando estrictamente 'pending_invitation_token', tema,
 * preferencias de usuario y filtros de dashboard.
 */
export async function purgeGhostSession() {
  try {
    if (typeof localStorage !== 'undefined') {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.startsWith('supabase.'))) {
          keysToRemove.push(key);
        }
      }
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') || key.startsWith('supabase.')) {
          if (!keysToRemove.includes(key)) keysToRemove.push(key);
        }
      });
      keysToRemove.forEach(k => localStorage.removeItem(k));
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem('onboarding_step');
      localStorage.removeItem(VIEW_AS_ROLE_KEY);
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('auth_retry_fix');
    }
    await removeOfflineCache();
  } catch (err) {
    console.warn("purgeGhostSession warning:", err);
  }
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRole, setUserRole] = useState<UserRole | null>(null);
    const [currentTechnicianId, setCurrentTechnicianId] = useState<string | null>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [mfaFactors, setMfaFactors] = useState<any[]>([]);
    const [mfaRequired, setMfaRequired] = useState(false);

    const [viewAsRole, setViewAsRole] = useState<UserRole | null>(() => {
        try {
            if (typeof localStorage !== 'undefined') {
                const saved = localStorage.getItem(VIEW_AS_ROLE_KEY);
                if (saved === 'technician') return 'technician';
            }
        } catch (e) {
            console.warn('Failed to read viewAsRole from localStorage', e);
        }
        return null;
    });

    // Anti-escalation formula: only an admin can view as technician; real technicians can NEVER view as admin.
    const effectiveRole: UserRole | null = (userRole === 'admin' && viewAsRole === 'technician') ? 'technician' : userRole;

    const toggleViewRole = () => {
        if (userRole !== 'admin') return;
        setViewAsRole(prev => {
            const next = prev === 'technician' ? null : 'technician';
            try {
                if (typeof localStorage !== 'undefined') {
                    if (next) {
                        localStorage.setItem(VIEW_AS_ROLE_KEY, next);
                    } else {
                        localStorage.removeItem(VIEW_AS_ROLE_KEY);
                    }
                }
            } catch (e) {
                console.warn('Failed to save viewAsRole to localStorage', e);
            }
            return next;
        });
    };

    useEffect(() => {
        // FIX: Capture the cleanup function outside the async scope so useEffect
        // can return it synchronously. Without this, subscription.unsubscribe() was
        // never called, causing a memory leak on every hot-reload or remount.
        let unsubscribe: (() => void) | undefined;

        // FIX: Guard against race conditions — if two auth events fire in quick
        // succession, only one fetchUserProfile should run at a time.
        let isHandlingSession = false;

        const initializeAuth = async () => {
            // Safety fallback: Ensure isLoading is cleared if supabase hangs
            const timeout = setTimeout(() => {
                setIsLoading(prev => {
                    if (prev) {
                        console.warn("AuthContext: initialization took too long, forcing isLoading to false.");
                        return false;
                    }
                    return prev;
                });
            }, 10000);

            // Check active session safely
            try {
                // V2291: CRITICAL - If we are on the /verified page with a code/token, 
                // skip getSession() to prevent Supabase Auth from auto-exchanging the code
                // which often fails with "both auth code and code verifier should be non-empty".
                const isVerificationPage = window.location.pathname.includes('/verified') || window.location.hash.includes('type=signup');
                const hasCode = window.location.search.includes('code=') || window.location.hash.includes('code=') || window.location.search.includes('token=');
                
                if (isVerificationPage && hasCode) {
                    console.log("AuthContext: Verification page detected, skipping getSession to avoid PKCE auto-exchange.");
                    setIsLoading(false); // Let Verified.tsx handle it
                    return;
                }

                const { data: { session }, error } = await supabase.auth.getSession();
                
                if (error) {
                    console.warn("AuthContext: getSession error:", error.message);
                    
                    const isTerminalError = 
                        (error as any).code === 'user_not_found' ||
                        error.message.includes('sub claim in JWT does not exist') || 
                        error.message.includes('User from sub claim') ||
                        error.message.includes('Invalid Refresh Token');

                    // Si el usuario fue borrado del backend pero el JWT sigue en localStorage
                    if (isTerminalError) {
                        console.warn("AuthContext: Invalid token detected on getSession, forcing surgical cleanup...");
                        await purgeGhostSession();
                        resetAuth();
                        setIsLoading(false);
                        return;
                    }

                    if (isNetworkError(error)) {
                        console.log("AuthContext: Network error on initializeAuth, checking offline cache...");
                        const cachedSession = await getOfflineCache();
                        if (cachedSession) {
                            console.log("AuthContext: Restoring from offline cache", cachedSession);
                            setIsAuthenticated(true);
                            setUser(cachedSession.user as any);
                            setUserEmail(cachedSession.user.email || null);
                            setUserRole(cachedSession.role);
                            setCurrentTechnicianId(cachedSession.currentTechnicianId);
                            if (cachedSession.companyId) {
                                setCompany({ id: cachedSession.companyId, name: cachedSession.companyName || 'Cargando...' });
                            }
                            setIsLoading(false);
                            return;
                        }
                    }
                }
                
                if (session) {
                    // V2325: Validación ÚNICA de red en arranque para detectar sesiones fantasma (usuario borrado en backend)
                    // Respeta 100% el modo offline: si no hay red o está forzado en tests, NO llama a la red
                    const isOffline = (typeof window !== 'undefined' && (window as any).__TEST_FORCE_OFFLINE__ === true) ||
                                      (typeof navigator !== 'undefined' && !navigator.onLine);

                    let sessionToUse = session;

                    if (!isOffline) {
                        try {
                            const { data: { user: verifiedUser }, error: userError } = await supabase.auth.getUser();
                            
                            if (userError) {
                                const isTerminalAuthError = 
                                    (userError as any).code === 'user_not_found' ||
                                    userError.message?.includes('sub claim in JWT does not exist') ||
                                    userError.message?.includes('User from sub claim') ||
                                    userError.message?.includes('Invalid Refresh Token');

                                if (isTerminalAuthError) {
                                    console.warn("AuthContext: Ghost session detected (user deleted in backend), surgical purge...", userError.message);
                                    await purgeGhostSession();
                                    resetAuth();
                                    setIsLoading(false);
                                    return;
                                }
                                // Si es error de red o 5xx, JAMÁS purgar: se preserva la sesión y modo offline
                            } else if (verifiedUser) {
                                sessionToUse = { ...session, user: verifiedUser };
                            }
                        } catch (netErr) {
                            console.warn("AuthContext: Network check in initializeAuth skipped:", netErr);
                        }
                    }

                    await handleUserSession(sessionToUse);
                } else {
                    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
                    if (isOffline) {
                        console.log("AuthContext: Offline detected with null session, checking offline cache...");
                        const cachedSession = await getOfflineCache();
                        if (cachedSession) {
                            console.log("AuthContext: Restoring from offline cache (offline mode)", cachedSession);
                            setIsAuthenticated(true);
                            setUser(cachedSession.user as any);
                            setUserEmail(cachedSession.user.email || null);
                            setUserRole(cachedSession.role);
                            setCurrentTechnicianId(cachedSession.currentTechnicianId);
                            if (cachedSession.companyId) {
                                setCompany({ id: cachedSession.companyId, name: cachedSession.companyName || 'Cargando...' });
                            }
                            setIsLoading(false);
                            return;
                        }
                    }
                    resetAuth();
                    setIsLoading(false);
                }
            } catch (e: any) {
                console.error("AuthContext: Error during initialization", e);
                
                // V65: Handle AbortError gracefully WITHOUT infinite loops
                const isAbort = e?.name === 'AbortError' || e?.message?.includes('aborted');
                
                if (isAbort && !sessionStorage.getItem('auth_retry_fix')) {
                    console.warn("AuthContext: AbortError detected. Attempting one-time cleanup of auth cache...");
                    sessionStorage.setItem('auth_retry_fix', 'true');
                    await removeOfflineCache();
                    const dataCacheKeys = ['pd_cached_user', 'pd_cached_technicians', 'pd_cached_settings', 'pd_memory_queue'];
                    dataCacheKeys.forEach(key => {
                        localStorage.removeItem(key);
                    });
                    if (Capacitor.isNativePlatform()) {
                        await Promise.all(dataCacheKeys.map(key => Preferences.remove({ key })));
                    }
                    Object.keys(localStorage).forEach(key => {
                        if (key.startsWith('sb-') || key.startsWith('supabase.')) {
                            localStorage.removeItem(key);
                        }
                    });
                    
                    if ('serviceWorker' in navigator) {
                        const regs = await navigator.serviceWorker.getRegistrations();
                        await Promise.all(regs.map(r => r.unregister()));
                    }
                    window.location.reload();
                    return;
                }
                
                resetAuth();
                setIsLoading(false);
            } finally {
                clearTimeout(timeout);
            }

            // Listen for changes
            const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
                console.log("AuthContext: onAuthStateChange event:", event);

                if (event === 'PASSWORD_RECOVERY') {
                    console.log("AuthContext: Password recovery detected.");
                    return;
                }

                if (event === 'SIGNED_IN' && (window.location.hash.includes('type=recovery') || window.location.pathname.includes('reset-password'))) {
                    console.log("AuthContext: Recovery session detected.");
                    return;
                }

                if (event === 'INITIAL_SESSION' && !session && (window.location.pathname.includes('verified') || window.location.hash.includes('type=signup'))) {
                    console.log("AuthContext: Initial null session on verification page, waiting for component to handle it.");
                    return;
                }

                // FIX: Guard against parallel invocations of handleUserSession
                // (e.g. INITIAL_SESSION + SIGNED_IN firing in quick succession).
                // CRITICAL: NEVER skip SIGNED_OUT. If we skip SIGNED_OUT, the app stays logged in forever!
                if (isHandlingSession && event !== 'SIGNED_OUT') {
                    console.log("AuthContext: Skipping duplicate auth event while session is being handled:", event);
                    return;
                }
                isHandlingSession = true;

                // V2164: Profile fetch is handled INSIDE handleUserSession
                const abortController = new AbortController();
                // If we get a new event before the previous handle finishes, we could cancel the previous one
                // However, we only have one session listener, we'll just pass a signal
                // to avoid doing work if unmounted. We'll store it on the effect level.
                
                handleUserSession(session, abortController.signal)
                    .catch(err => console.error("AuthContext: Error in decoupled handleUserSession:", err))
                    .finally(() => { isHandlingSession = false; });

                // V2165: Check for MFA Requirement
                if (event === 'MFA_CHALLENGE_VERIFIED' || event === 'SIGNED_IN') {
                    refreshMfaFactors().catch(err => {
                        console.error("AuthContext: Error in decoupled refreshMfaFactors:", err);
                    });
                }
            });

            // FIX: Store the unsubscribe ref so the useEffect cleanup can call it.
            unsubscribe = () => subscription.unsubscribe();
        };

        initializeAuth();

        // FIX: This cleanup now correctly unsubscribes from the Supabase auth listener
        // on component unmount (or hot-reload), preventing the memory leak.
        return () => unsubscribe?.();
    }, []);

    const handleUserSession = async (session: Session | null, abortSignal?: AbortSignal) => {
        if (session) {
            console.log("AuthContext: Handling user session for:", session.user.id, session.user.email);
            
            setIsAuthenticated(true);
            setUserEmail(session.user.email || null);
            setUser(session.user);
            
            // V2164: CRITICAL FIX - Await profile fetch BEFORE clearing loading state
            // This prevents the App.tsx from seeing isAuthenticated=true with userRole=null
            // or an incorrect optimistic role.
            try {
                await fetchUserProfile(session.user, abortSignal);
                await refreshMfaFactors();
            } catch (e) {
                console.error("AuthContext: fetchUserProfile error", e);
            } finally {
                setIsLoading(false);
            }
        } else {
            resetAuth();
            setIsLoading(false);
        }
    };

    const refreshMfaFactors = async () => {
        try {
            const { data, error } = await supabase.auth.mfa.listFactors();
            if (error) throw error;
            const allFactors = data?.all || [];
            setMfaFactors(allFactors);
            
            // FIX: mfaRequired was a zombie state — setMfaRequired was never called.
            // Now it correctly reflects whether the user has at least one verified TOTP factor.
            const hasVerifiedFactor = allFactors.some(f => f.status === 'verified');
            setMfaRequired(hasVerifiedFactor);
        } catch (e: any) {
            // Silently ignore "User does not exist" errors in non-critical MFA listing
            if (e?.message?.includes('sub claim in JWT does not exist')) {
                console.debug("AuthContext: MFA listing skipped - invalid session detected");
            } else {
                console.warn("AuthContext: Failed to list MFA factors", e);
            }
        }
    };

    const enrollTOTP = async () => {
        const { data, error } = await supabase.auth.mfa.enroll({
            factorType: 'totp',
            issuer: 'Parte Diario App'
        });
        if (error) throw error;
        return {
            id: data.id,
            qrCodeUrl: data.totp.qr_code,
            uri: data.totp.uri
        };
    };

    const verifyTOTP = async (factorId: string, code: string) => {
        const { data, error } = await supabase.auth.mfa.challengeAndVerify({
            factorId,
            code
        });
        if (error) throw error;
        await refreshMfaFactors();
    };

    const unenrollFactor = async (factorId: string) => {
        const { error } = await supabase.auth.mfa.unenroll({ factorId });
        if (error) throw error;
        await refreshMfaFactors();
    };

    const [company, setCompany] = useState<any>(null);

    const fetchUserProfile = async (currentUser: User, abortSignal?: AbortSignal) => {
        // V2150: Prioritize metadata from the trigger/JWT (now synced)
        const jwtRole = currentUser.app_metadata?.role || currentUser.user_metadata?.role;
        const jwtCompanyId = currentUser.app_metadata?.company_id || currentUser.user_metadata?.company_id;
        
        console.log("AuthContext: Metadata found:", { jwtRole, jwtCompanyId });

        if (jwtRole === 'technician') {
            setUserRole('technician');
        } else if (jwtRole === 'admin') {
            setUserRole('admin');
        }

        try {
            let profile = null;
            let attempts = 0;
            const maxAttempts = 3;
            let lastError: any = null;

            // V2164: Optimization - if we already have the role and companyId from JWT,
            // we can be more optimistic, but we still fetch the profile to get full company details.
            while (attempts < maxAttempts && !profile) {
                if (abortSignal?.aborted) return;
                try {
                    const { data, error } = await supabase
                        .from('technicians')
                        .select('*, company:companies(*)')
                        .or(`id.eq.${currentUser.id},auth_user_id.eq.${currentUser.id}`)
                        .single();

                    if (error) {
                        if (error.code === 'PGRST116') {
                            // If not found yet, maybe the trigger is slow
                            attempts++;
                            if (attempts < maxAttempts) {
                                await new Promise(r => setTimeout(r, 800 * attempts));
                            }
                            continue;
                        }
                        throw error;
                    }
                    profile = data;
                } catch (e) {
                    lastError = e;
                    attempts++;
                    if (attempts < maxAttempts) {
                        await new Promise(r => setTimeout(r, 800 * attempts));
                    }
                }
            }

            const isOffline = (typeof window !== 'undefined' && (window as any).__TEST_FORCE_OFFLINE__ === true) ||
                              (typeof navigator !== 'undefined' && !navigator.onLine);

            if (!profile && (isOffline || isNetworkError(lastError))) {
                throw lastError || new Error('Network error loading profile');
            }

            if (profile) {
                // DB profile is source of truth for role
                const dbRole = profile.role === 'admin' ? 'admin' : 'technician';
                setUserRole(dbRole as UserRole);
                setCurrentTechnicianId(profile.id);
                setCompany(profile.company);
                telemetry.identify(profile.id, currentUser.email || '', dbRole);

                await setOfflineCache({
                    user: {
                        id: currentUser.id,
                        email: currentUser.email || '',
                        app_metadata: currentUser.app_metadata || {},
                        user_metadata: currentUser.user_metadata || {}
                    },
                    role: dbRole,
                    currentTechnicianId: profile.id,
                    companyId: profile.company?.id || null,
                    companyName: profile.company?.name || null,
                    timestamp: Date.now()
                });
            } else if (jwtRole === 'technician' && jwtCompanyId) {
                // V_CRITICAL_FIX: JWT says technician but DB profile not found after 3 attempts.
                // This happens when the JWT is stale and RLS blocks the self-lookup.
                // Force a session refresh and try ONE more time before giving up.
                console.warn("AuthContext: DB profile not found after retries. Forcing JWT refresh for technician...");
                try {
                    const { data: refreshData } = await supabase.auth.refreshSession();
                    const freshUser = refreshData?.session?.user;
                    if (freshUser && !abortSignal?.aborted) {
                        const { data: retryData } = await supabase
                            .from('technicians')
                            .select('*, company:companies(*)')
                            .or(`id.eq.${freshUser.id},auth_user_id.eq.${freshUser.id}`)
                            .single();
                        if (retryData) {
                            const dbRole = retryData.role === 'admin' ? 'admin' : 'technician';
                            setUserRole(dbRole as UserRole);
                            setCurrentTechnicianId(retryData.id);
                            setCompany(retryData.company);
                            setUser(freshUser);
                            console.log("AuthContext: Profile recovered after JWT refresh ✓");
                            telemetry.identify(retryData.id, freshUser.email || '', dbRole);

                            await setOfflineCache({
                                user: {
                                    id: freshUser.id,
                                    email: freshUser.email || '',
                                    app_metadata: freshUser.app_metadata || {},
                                    user_metadata: freshUser.user_metadata || {}
                                },
                                role: dbRole,
                                currentTechnicianId: retryData.id,
                                companyId: retryData.company?.id || null,
                                companyName: retryData.company?.name || null,
                                timestamp: Date.now()
                            });
                            return;
                        }
                    }
                } catch (refreshErr) {
                    console.warn("AuthContext: JWT refresh attempt failed:", refreshErr);
                }
                // Last resort: trust JWT role but leave currentTechnicianId null
                // At least the user won't be stuck in a loop — JoinCompany will handle redirection
                setUserRole(jwtRole as UserRole);
                setCompany({ id: jwtCompanyId, name: 'Cargando...' });
                console.error("AuthContext: Could not recover technician profile. User may need to re-login.");
                telemetry.identify(currentUser.id, currentUser.email || '', jwtRole);

                await setOfflineCache({
                    user: {
                        id: currentUser.id,
                        email: currentUser.email || '',
                        app_metadata: currentUser.app_metadata || {},
                        user_metadata: currentUser.user_metadata || {}
                    },
                    role: jwtRole,
                    currentTechnicianId: null,
                    companyId: jwtCompanyId,
                    companyName: 'Cargando...',
                    timestamp: Date.now()
                });
            } else if (jwtRole) {
                // JWT has role but no DB profile yet — trust JWT role
                setUserRole(jwtRole as UserRole);
                if (jwtCompanyId) {
                    setCompany({ id: jwtCompanyId, name: 'Cargando...' });
                }
                telemetry.identify(currentUser.id, currentUser.email || '', jwtRole);

                await setOfflineCache({
                    user: {
                        id: currentUser.id,
                        email: currentUser.email || '',
                        app_metadata: currentUser.app_metadata || {},
                        user_metadata: currentUser.user_metadata || {}
                    },
                    role: jwtRole,
                    currentTechnicianId: null,
                    companyId: jwtCompanyId || null,
                    companyName: jwtCompanyId ? 'Cargando...' : null,
                    timestamp: Date.now()
                });
            }
            // FIX: If neither DB profile nor JWT role exists, leave role as null.
            // This prevents falsely triggering needsOnboarding for technicians
            // whose JWT hasn't propagated yet.

        } catch (err: any) {
            console.error("AuthContext: Unexpected error in fetchUserProfile:", err);
            
            if (isNetworkError(err)) {
                const cached = await getOfflineCache();
                if (cached && cached.user.id === currentUser.id) {
                    console.log("AuthContext: fetchUserProfile failed offline, restoring profile from cache.");
                    setUserRole(cached.role);
                    setCurrentTechnicianId(cached.currentTechnicianId);
                    if (cached.companyId) {
                        setCompany({ id: cached.companyId, name: cached.companyName || 'Cargando...' });
                    }
                    return;
                }
            }
            
            // FIX: Don't default to 'admin' on error — keeps null to avoid false onboarding trigger
            if (jwtRole) setUserRole(jwtRole as UserRole);
        }
    };

    const refreshCompany = async () => {
        // FIX: Force a JWT refresh FIRST so the new app_metadata (company_id, role)
        // written by accept_invitation_sd is available in the token before we re-fetch
        // the profile. Without this, the RLS policies see the OLD JWT with no company_id.
        try {
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
                console.warn('AuthContext: refreshSession error (non-fatal):', refreshError.message);
            }
            const freshUser = refreshData?.session?.user;
            if (freshUser) {
                // Also update the user state so downstream reads see fresh metadata
                setUser(freshUser);
                await fetchUserProfile(freshUser);
            } else if (user) {
                await fetchUserProfile(user);
            }
        } catch (e) {
            console.error('AuthContext: refreshCompany error', e);
            if (user) await fetchUserProfile(user);
        }
    };

    const resetAuth = () => {
        setIsAuthenticated(false);
        setUserRole(null);  // FIX: Don't default to 'admin' — let fetchUserProfile determine the real role
        setCurrentTechnicianId(null);
        setUserEmail(null);
        setUser(null);
        setMfaFactors([]);
        setMfaRequired(false);
        setCompany(null);
        
        // FIX: Limpiar caché de React Query para evitar estados zombie
        // tras signOut programático (recuperación de contraseña)
        getQueryClient().clear();

        // FIX: Clear onboarding storage key on every auth reset (not just on invalid token).
        // Leaving stale keys can cause the onboarding wizard to re-appear after a clean logout.
        sessionStorage.removeItem('auth_retry_fix');
        localStorage.removeItem('onboarding_step');
        localStorage.removeItem(VIEW_AS_ROLE_KEY);
        setViewAsRole(null);
        removeOfflineCache().catch(err => console.warn("resetAuth removeOfflineCache failed:", err));
    };

    const login = (role: UserRole) => {
        // Intentionally empty: authentication is fully handled by Supabase Auth listeners.
        // If you're calling this manually expecting state changes, use supabase.auth.signInWithPassword() instead.
        if (process.env.NODE_ENV !== 'production') {
            console.warn("AuthContext: login() stub called with role:", role, "— this function is a no-op.");
        }
    };

    const logout = async () => {
        console.log("AuthContext: Logging out...");
        try {
            telemetry.reset();
            await Promise.race([
                supabase.auth.signOut(),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Logout Timeout')), 2000))
            ]);
        } catch (error) {
            console.warn("SignOut error/timeout:", error);
        } finally {
            resetAuth();
            // Use replace so the logged-out state replaces the previous history entry,
            // preventing the back button from returning to authenticated routes.
            setTimeout(() => {
                window.location.replace('/');
            }, 100);
        }
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated, 
            userRole, 
            currentTechnicianId, 
            userEmail, 
            login, 
            logout,
            isLoading,
            user,
            setUserRole,
            mfaFactors,
            mfaRequired,
            refreshMfaFactors,
            enrollTOTP,
            verifyTOTP,
            unenrollFactor,
            company,
            refreshCompany,
            viewAsRole,
            effectiveRole,
            toggleViewRole
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
